/**
 * Stable browser-session id (#35 session work) — the single id that ties a real user's
 * RUM browser session ↔ the backend HTTP/WS traces their requests drive ↔ the generation
 * agent's conversation. It is minted once per browser session and reused thereafter.
 *
 * SEMANTIC (the reframe): this id represents the USER'S BROWSER SESSION — it spans the
 * multiple games and page views a user goes through in one sitting — NOT a single game.
 * The per-game grain stays the `gameId` (carried as the `jeopardy.game_id` span attribute
 * on the backend), so `session.id` is no longer overloaded with the game id.
 *
 * ALIGNMENT WITH RUM: the value is minted HERE (client-owned) and then PINNED into the
 * `aws-rum-web` client (`pinSessionId`, which does NOT re-roll sampling), so RUM's own
 * session id becomes this same value and every RUM event carries it natively. It is ALSO
 * attached as the `session_id` RUM session attribute for explicit queryability. Because we
 * own the id (rather than reading RUM's lazily-minted one), it is available synchronously
 * before RUM finishes its async init — so the WS `?sid=` param and the `x-jeopardy-session-id`
 * HTTP header always carry a value that matches what RUM ends up reporting.
 *
 * GRAIN: persisted in `sessionStorage`, so it is stable across page navigations/reloads
 * within a browsing session and resets when the session ends — matching "one browser
 * session" rather than a forever id (that would be the cross-session `funnelKey`).
 *
 * Degrade-safe: `sessionStorage` can throw (private mode, disabled storage, SSR). Any
 * failure falls back to a per-load in-memory id so callers always get a usable value and
 * telemetry NEVER breaks the page (the same no-throw discipline as `funnel-key.ts` /
 * `rum.ts`).
 *
 * PRIVACY (#35): an opaque first-party UUID — never PII, never a cookie. Consistent with
 * the owner decision that span/RUM content capture is ON.
 */

const STORAGE_KEY = 'jeopardy.sessionId';

/** In-memory fallback when `sessionStorage` is unavailable, stable within a page load. */
let memoryId: string | null = null;

/** Mint a UUID, falling back to a non-crypto id if `crypto.randomUUID` is unavailable. */
function mintId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the non-crypto fallback
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Read the stable browser-session id, minting + persisting it on first use. Memoized
 * within a load via the in-memory fallback, so repeated calls return the same value even
 * when `sessionStorage` is unavailable.
 */
export function getSessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const minted = mintId();
    window.sessionStorage.setItem(STORAGE_KEY, minted);
    return minted;
  } catch {
    // sessionStorage unavailable (private mode / disabled / SSR) — use a stable per-load
    // in-memory id so correlation still has a key, without ever throwing.
    memoryId ??= mintId();
    return memoryId;
  }
}

/** Test-only: drop the in-memory fallback so each test starts clean. */
export function resetSessionIdForTests(): void {
  memoryId = null;
}
